/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { useEffect, useMemo } from 'react';
import { useQueries, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getAllMeterSubTypes, getMeterSubType, getRegisterCatalogue } from '../../api/ami';
import { ApiError } from '../../api/client';
import { buildMeterTypeIndex, commonRegisters, toMeterType, type MeterType } from './meterTypes';
import { toChartParameters, toRegisters } from './catalogue';

export const REGISTER_CATALOGUE_QUERY_KEY = ['ami', 'registers'];
export const METER_TYPES_QUERY_KEY = ['ami', 'subtypes'];

const SESSION = {
  staleTime: Infinity,
  gcTime: Infinity,
  retry: (failures: number, error: Error) => failures < 1 && !(error instanceof ApiError && error.status < 500),
} as const;

const registerCatalogueQuery = {
  queryKey: REGISTER_CATALOGUE_QUERY_KEY,
  queryFn: async () => toRegisters(await getRegisterCatalogue()),
  ...SESSION,
};

export const meterTypesQuery = {
  queryKey: METER_TYPES_QUERY_KEY,
  queryFn: async () => buildMeterTypeIndex(await getAllMeterSubTypes()),
  ...SESSION,
};

export function meterTypeQuery(id: string) {
  return { queryKey: ['ami', 'subtype', id], queryFn: async () => toMeterType(await getMeterSubType(id)), ...SESSION };
}

export function usePrefetchAmiReferenceData(enabled: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    void queryClient.prefetchQuery(registerCatalogueQuery);
    void queryClient.prefetchQuery(meterTypesQuery);
  }, [enabled, queryClient]);
}

export function useRegisterCatalogue() {
  return useQuery(registerCatalogueQuery);
}

export function useMeterTypes() {
  return useQuery(meterTypesQuery);
}

function combineMeterTypes(results: UseQueryResult<MeterType | null>[]) {
  const byId = new Map<string, MeterType>();
  results.forEach((result) => {
    if (result.data) byId.set(result.data.id, result.data);
  });
  return { byId, isLoading: results.some((result) => result.isLoading) };
}

export function useMeterTypeRegisters(subTypeIds: (string | null | undefined)[]) {
  const ids = [...new Set(subTypeIds.filter((id): id is string => !!id))].sort();
  const index = useMeterTypes();
  const missing = index.isSuccess ? ids.filter((id) => !index.data.has(id)) : index.isError ? ids : [];
  const fallback = useQueries({ queries: missing.map(meterTypeQuery), combine: combineMeterTypes });
  const isLoading = (ids.length > 0 && index.isLoading) || fallback.isLoading;

  const idsKey = ids.join(',');
  const codes = useMemo(
    () => commonRegisters(idsKey.split(',').filter(Boolean).map((id) => index.data?.get(id) ?? fallback.byId.get(id))),
    [idsKey, index.data, fallback.byId],
  );
  return { codes, isLoading };
}

export function useChartParameters(
  subTypeIds: (string | null | undefined)[] = [],
  readingCodes: ReadonlySet<string> | null = null,
) {
  const { t } = useTranslation();
  const catalogue = useRegisterCatalogue();
  const types = useMeterTypeRegisters(subTypeIds);
  const fromReadings = readingCodes !== null && readingCodes.size > 0;
  const available = fromReadings ? readingCodes : types.codes;
  const waiting = !fromReadings && types.isLoading;
  const parameters = useMemo(() => {
    if (!catalogue.data || waiting) return [];
    const registers = available ? catalogue.data.filter((register) => available.has(register.code)) : catalogue.data;
    return toChartParameters(registers, (title) => t('label_phase_parameter_title', { title }));
  }, [catalogue.data, available, waiting, t]);
  return { ...catalogue, parameters, typesLoading: waiting };
}
